import { motion } from 'framer-motion';
import React, { ReactElement } from 'react';
import { Link, generatePath, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ImportCurve, Eye, MoreVertical, Trash, User } from '@/components/icons';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { CardActionButton } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDateOnly } from '@/lib/date';
import { cn } from '@/lib/utils';
import { PatientDetailPage } from '@/pages/constants';
import { ReportStatusBadge } from '@/pages/patients/components/StatusBadges';
import { canExportReport, getAge, getReportLink, translateGender } from '@/utils';
import { ReportItemProps } from './types';

const getAppliedPeriod = (report: ReportItemProps['report']) => {
    const start = report.tests_performed_at_start;
    const end = report.tests_performed_at_end;

    if (start && end) {
        return `${formatDateOnly(start)} - ${formatDateOnly(end)}`;
    }

    return start ? formatDateOnly(start) : undefined;
};

function ReportGridItem({
    report,
    reportStatus,
    onExport,
    onDelete,
}: ReportItemProps): ReactElement {
    const navigate = useNavigate();
    const location = useLocation();
    const patientId = report.patient?.id;
    const backTo = `${location.pathname}${location.search}`;
    const currentStatus = report.status || reportStatus;
    const primaryActionLabel = 'Abrir laudo';
    const appliedPeriod = getAppliedPeriod(report);
    const exportEnabled = canExportReport(report);
    const demographicSummary = [
        report.patient?.date_of_birth ? `${getAge(report.patient.date_of_birth)} anos` : undefined,
        report.patient?.gender ? translateGender(report.patient.gender) : undefined,
        report.patient?.city,
    ]
        .filter(Boolean)
        .join(' • ');
    const pendingConsolidationsCount = report.pending_consolidations?.length ?? 0;
    const pendingPublisherApprovalsCount = report.pending_publisher_result_approvals_count ?? 0;
    const pendingReviewCount = pendingConsolidationsCount + pendingPublisherApprovalsCount;
    const pendingReviewMessage =
        pendingReviewCount === 1
            ? 'Há 1 resultado pronto para revisão antes de ser adicionado ao laudo.'
            : `Há ${pendingReviewCount} resultados prontos para revisão antes de serem adicionados ao laudo.`;

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
            }}
        >
            <Card
                className="group relative h-full cursor-pointer overflow-hidden border border-border/80 bg-card/95 shadow-elevation-1 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand/20 hover:shadow-elevation-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                onClick={() => {
                    if (!report?.id) return;
                    navigate(getReportLink(report.id));
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    if (!report?.id) return;
                    navigate(getReportLink(report.id));
                }}
            >
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${
                        currentStatus === 'completed'
                            ? 'from-success/18 via-success/0 to-transparent'
                            : 'from-info/18 via-info/0 to-transparent'
                    }`}
                />

                <div className="relative flex h-full flex-col p-5">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex min-h-10 items-center">
                            <ReportStatusBadge status={currentStatus} />
                        </div>

                        <DropdownMenu>
                            <DropdownMenuTrigger
                                asChild
                                onClick={(event: React.MouseEvent) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                            >
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="size-10 flex-shrink-0"
                                >
                                    <MoreVertical className="size-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                            >
                                <DropdownMenuItem asChild>
                                    <Link
                                        to={getReportLink(report?.id)}
                                        onClick={(event: React.MouseEvent) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        <Eye className="mr-2 size-4" />
                                        Abrir laudo
                                    </Link>
                                </DropdownMenuItem>
                                {exportEnabled && (
                                    <DropdownMenuItem
                                        onClick={(event: React.MouseEvent) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onExport();
                                        }}
                                        className="justify-between gap-3"
                                    >
                                        <span className="inline-flex items-center">
                                            <ImportCurve className="mr-2 size-4" />
                                            Baixar em .docx
                                        </span>
                                    </DropdownMenuItem>
                                )}
                                {patientId && (
                                    <DropdownMenuItem asChild>
                                        <Link
                                            to={`/${generatePath(PatientDetailPage, {
                                                patientId,
                                            })}`}
                                            state={{ backTo }}
                                            onClick={(event: React.MouseEvent) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <User className="mr-2 size-4" />
                                            Abrir paciente
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={(event: React.MouseEvent) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onDelete();
                                    }}
                                    className="text-red-600 focus:text-red-600"
                                >
                                    <Trash className="mr-2 size-4" />
                                    Excluir
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <PatientAvatar
                            name={report.patient?.full_name || ''}
                            avatarUrl={report.patient?.avatar_url}
                            className="size-12 rounded-2xl"
                        />

                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                            {patientId ? (
                                <Link
                                    to={`/${generatePath(PatientDetailPage, {
                                        patientId,
                                    })}`}
                                    state={{ backTo }}
                                    onClick={(event: React.MouseEvent) => event.stopPropagation()}
                                    className="block max-w-full"
                                >
                                    <h3 className="truncate text-lg font-semibold text-foreground transition-colors group-hover:text-brand">
                                        {report.patient?.full_name || 'Paciente não vinculado'}
                                    </h3>
                                </Link>
                            ) : (
                                <h3 className="truncate text-lg font-semibold text-foreground">
                                    {report.patient?.full_name || 'Paciente não vinculado'}
                                </h3>
                            )}
                            {pendingReviewCount > 0 && (
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                className={cn(
                                                    'inline-flex w-fit items-center rounded-md border border-warning/20 bg-warning/15 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-warning transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                                                    'cursor-help hover:bg-warning/20',
                                                )}
                                                onClick={(
                                                    event: React.MouseEvent<HTMLButtonElement>,
                                                ) => event.stopPropagation()}
                                                onKeyDown={(
                                                    event: React.KeyboardEvent<HTMLButtonElement>,
                                                ) => event.stopPropagation()}
                                            >
                                                {pendingReviewCount} resultado
                                                {pendingReviewCount > 1 ? 's' : ''} p/ revisão
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="bottom"
                                            align="start"
                                            className="max-w-80 whitespace-normal px-3 py-2 text-sm leading-relaxed"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            {pendingReviewMessage}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <Clock className="size-4 text-muted-foreground/80" />
                            <span>Atualizado em {formatDateOnly(report.updated_at)}</span>
                        </div>

                        {appliedPeriod && (
                            <div className="flex items-center gap-2">
                                <Calendar className="size-4 text-muted-foreground/80" />
                                <span>Período {appliedPeriod}</span>
                            </div>
                        )}

                        {demographicSummary && (
                            <div className="flex items-center gap-2">
                                <User className="size-4 text-muted-foreground/80" />
                                <span>{demographicSummary}</span>
                            </div>
                        )}
                    </div>

                    <div className="mt-auto pt-6">
                        <div className="flex flex-col gap-2 border-t border-border/70 pt-5 sm:flex-row">
                            <CardActionButton
                                to={getReportLink(report.id)}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {primaryActionLabel}
                            </CardActionButton>

                            {exportEnabled && (
                                <Button
                                    className="w-full justify-between"
                                    onClick={(event: React.MouseEvent) => {
                                        event.stopPropagation();
                                        onExport();
                                    }}
                                >
                                    Baixar laudo
                                    <ImportCurve className="size-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
}

export default ReportGridItem;
